// src/pages/finance-portal/loans/Loans.js
//
// Loans list page

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, Chip, Alert, Skeleton, Tabs, Tab, InputAdornment,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    ACTIVE:     { bg: '#dcfce7', color: '#166534' },
    PAID_OFF:   { bg: '#e0e7ff', color: '#4338ca' },
    DEFAULTED:  { bg: '#fecaca', color: '#991b1b' },
    REFINANCED: { bg: '#fef9c3', color: '#854d0e' },
    CLOSED:     { bg: '#f1f5f9', color: '#475569' },
};

const STATUS_TABS = ['ALL', 'ACTIVE', 'PAID_OFF', 'CLOSED', 'DEFAULTED'];

function fmtMoney(val, currency = 'USD') {
    return new Intl.NumberFormat('en-US', {
        style: 'currency', currency, maximumFractionDigits: 0,
    }).format(val || 0);
}

export default function Loans() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [statusTab, setStatusTab] = useState(0);

    const fetchLoans = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            const status = STATUS_TABS[statusTab];
            if (status !== 'ALL') params.status = status;
            if (search) params.search = search;
            const res = await axiosInstance.get('/api/finance/loans', { params });
            if (res.data.success) {
                setRows(res.data.data);
                setTotal(res.data.total);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load loans');
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, statusTab, search]);

    useEffect(() => { fetchLoans(); }, [fetchLoans]);

    const labelCell = { fontFamily, fontWeight: 700, fontSize: '0.75rem', color: '#64748b',
                        textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0',
                        letterSpacing: '0.05em' };
    const dataCell = { fontFamily, fontSize: '0.85rem', color: '#475569' };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)' }}>
                            <AccountBalanceIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white',
                                                          fontSize: { xs: '1.4rem', md: '1.85rem' } }}>
                                Loans & Debt
                            </Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)',
                                              fontSize: '0.9rem', mt: 0.25 }}>
                                Track loans, amortization schedules, and covenant compliance
                            </Typography>
                        </Box>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />}
                            onClick={() => navigate('/finance-portal/loans/new')}
                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                  bgcolor: 'rgba(255,255,255,0.2)', color: '#fff',
                                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>
                        New Loan
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)}
                                  sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <TextField size="small" placeholder="Search by number / name / lender..."
                               value={search}
                               onChange={e => { setSearch(e.target.value); setPage(0); }}
                               InputProps={{
                                   startAdornment: <InputAdornment position="start">
                                       <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                                   </InputAdornment>,
                                   sx: { fontFamily, fontSize: '0.85rem', borderRadius: '10px' },
                               }}
                               sx={{ minWidth: { xs: '100%', sm: 360 } }} />
                </Box>

                <Tabs value={statusTab} onChange={(_, v) => { setStatusTab(v); setPage(0); }}
                      sx={{ mb: 2,
                            '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none',
                                                fontSize: '0.85rem', color: '#64748b', minHeight: 40, py: 1 },
                            '& .Mui-selected': { color: ACCENT },
                            '& .MuiTabs-indicator': { bgcolor: ACCENT, height: 3, borderRadius: '3px' } }}>
                    {STATUS_TABS.map(t => <Tab key={t} label={t.replace('_', ' ')} />)}
                </Tabs>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                    {['Loan #', 'Name', 'Lender', 'Type', 'Original',
                                      'Balance', 'Rate', 'Maturity', 'Status'].map(h =>
                                        <TableCell key={h} sx={labelCell}>{h}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    [1,2,3].map(i => (
                                        <TableRow key={i}><TableCell colSpan={9}><Skeleton height={36} /></TableCell></TableRow>
                                    ))
                                ) : rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} sx={{ textAlign: 'center', py: 6 }}>
                                            <AccountBalanceIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>
                                                No loans found
                                            </Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.8rem', mt: 0.5 }}>
                                                Add your first loan to start tracking debt
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : rows.map(r => {
                                    const sStyle = STATUS_COLORS[r.status] || STATUS_COLORS.ACTIVE;
                                    return (
                                        <TableRow key={r.id} hover
                                                  onClick={() => navigate(`/finance-portal/loans/${r.id}`)}
                                                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' } }}>
                                            <TableCell sx={{ ...dataCell, fontWeight: 600, color: '#1e293b' }}>
                                                {r.loan_number}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, color: '#1e293b', fontWeight: 500 }}>
                                                {r.name}
                                            </TableCell>
                                            <TableCell sx={dataCell}>{r.lender}</TableCell>
                                            <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                                <Chip label={r.loan_type?.replace('_', ' ')} size="small"
                                                      sx={{ fontFamily, fontSize: '0.7rem', height: 20,
                                                            bgcolor: '#f1f5f9', color: '#475569' }} />
                                            </TableCell>
                                            <TableCell sx={dataCell}>
                                                {fmtMoney(r.original_amount, r.currency)}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, fontWeight: 600, color: '#1e293b' }}>
                                                {fmtMoney(r.current_balance, r.currency)}
                                            </TableCell>
                                            <TableCell sx={dataCell}>
                                                {r.interest_rate ? `${r.interest_rate}%` : '—'}
                                                {r.rate_type === 'VARIABLE' ? ' (V)' : ''}
                                            </TableCell>
                                            <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                                {r.maturity_date}
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={r.status?.replace('_', ' ')} size="small"
                                                      sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600,
                                                            height: 22, bgcolor: sStyle.bg, color: sStyle.color }} />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination component="div" count={total} page={page}
                                     onPageChange={(_, p) => setPage(p)}
                                     rowsPerPage={rowsPerPage}
                                     onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                     sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }} />
                </Card>
            </Box>
        </Box>
    );
}
